import { ENDCAP_POSITION_LOWER, ENDCAP_POSITION_UPPER, AUTOMATION_PROJECT_TYPE_CHAMBER } from "../lib/types";

/**
 * Background Class Model
 */
export class Background{

  /**
   *
   * @param x {number}
   * @param y {number}
   * @param scaleX {number}
   * @param scaleY {number}
   * @param rotation {number}
   * @param width {number}
   * @param height {number}
   * @param data {string | null}
   */
  constructor(
    x = 0,
    y = 0,
    scaleX = 0,
    scaleY = 0,
    rotation = 0,
    width = 0,
    height = 0,
    data = null
  ) {
    this.x = x;
    this.y = y;
    this.scaleX = scaleX;
    this.scaleY = scaleY;
    this.rotation = rotation;
    this.width = width;
    this.height = height;
    this.data = data;
  }
}

/**
 * Email Class Model
 */
export class Email{
  /**
   * @param to {string | null}
   * @param drawingsURL {string}
   * @param boomURL {string}
   * @param isInternal {boolean}
   * @param chamber {string}
   * @param outlet {string}
   * @param name {string}
   * @param engineer {string}
   * @param location {string}
   * @param requiredVolume {number}
   * @param porosity {number}
   * @param stoneBelow {number}
   * @param stoneAbove{number}
   * @param avgCover {number}
   * @param availableLength {number}
   * @param availableWidth {number}
   * @param installedVolume {number}
   * @param chamberStorage {number}
   * @param numOfChambers {number}
   * @param numOfEndCaps {number}
   * @param numOfRows {number}
   * @param avgRowLength {number}
   * @param maxLength {number}
   * @param maxWidth {number}
   * @param approxBedSize{number}
   * @param amountStone {number}
   * @param excavationVolume {number}
   * @param nonWovenRequired {number}
   * @param isolatorRowLength {number}
   * @param nonWovenIsolator {number}
   * @param wovenIsolator {number}
   */
  constructor(
    to = null,
    drawingsURL = "",
    boomURL = "",
    isInternal = true,
    chamber = "",
    outlet = "",
    name = "",
    engineer = "",
    location = "",
    requiredVolume = 0,
    porosity = 0,
    stoneBelow = 0,
    stoneAbove = 0,
    avgCover = 0,
    availableLength = 0,
    availableWidth = 0,
    installedVolume = 0,
    chamberStorage = 0,
    numOfChambers = 0,
    numOfEndCaps = 0,
    numOfRows = 0,
    avgRowLength = 0,
    maxLength = 0,
    maxWidth = 0,
    approxBedSize = 0,
    amountStone = 0,
    excavationVolume = 0,
    nonWovenRequired = 0,
    isolatorRowLength = 0,
    nonWovenIsolator = 0,
    wovenIsolator = 0
  ) {
    this.to = to;

    /* drawing and BOM urls */
    this.drawingsURL = drawingsURL;
    this.boomURL = boomURL;
    this.isInternal = isInternal;

    /* the numeric table values */
    this.chamber = chamber;
    this.outlet = outlet;
    this.name = name;
    this.engineer = engineer;
    this.location = location;
    this.requiredvolume = requiredVolume;
    this.porosity = porosity;
    this.stoneBelow = stoneBelow;
    this.stoneAbove = stoneAbove;
    this.avgCover = avgCover;
    this.availableLength = availableLength;
    this.availableWidth = availableWidth;
    this.installedVolume = installedVolume;
    this.chamberStorage = chamberStorage;
    this.numOfChambers = numOfChambers;
    this.numOfEndcaps = numOfEndCaps;
    this.numOfRows = numOfRows;
    this.avgRowLength = avgRowLength;
    this.maxLength = maxLength;
    this.maxWidth = maxWidth;
    this.approxBedSize = approxBedSize;
    this.amountStone = amountStone;
    this.excavationVolume = excavationVolume;
    this.nonWovenRequired = nonWovenRequired;
    this.isolatorRowLength = isolatorRowLength;
    this.nonWovenIsolator = nonWovenIsolator;
    this.wovenIsolator = wovenIsolator;
    this.date = new Date().toLocaleString("en-US").replace(",", "").replace(/(\s|\/)/g, "-");
  }
}

/**
 * StoneBed Class Model
 */
export class StoneBed{
  constructor() {
    this.points = [];
  }

  /**
   * Build the points object
   * @param index {number}
   * @param x {number}
   * @param y {number}
   * @returns {void}
   */
  addPoint( index, x, y ) {
    this.points[index] = { x, y };
  }
}

/**
 * ChamberBed Class Model
 */
export class ChamberBed{
  /**
   *
   * @param {number} x
   * @param {number} y
   * @param {number} rotation
   * @param {number} width
   * @param {number} height
   */
  constructor( x = 0, y = 0, rotation = 0, width = 0, height = 0 ) {
    this.x = x;
    this.y = y;
    this.rotation = rotation;
    this.width = width;
    this.height = height;
    this.rows = [];
  }

  addRow( i, row ) {
    this.rows[i] = row;
  }

  addChamber( row, col, chamber ) {
    this.rows[row].chambers.push(chamber);
  }

  addEndCap( row, col, position, endCap ) {
    /* the endcap property */
    let endCapProp;
    /* if upper endcap */
    if (position === ENDCAP_POSITION_UPPER) {
      endCapProp = "upperEndCap";
    } else if (position === ENDCAP_POSITION_LOWER) {
      /* lower endcap */
      endCapProp = "lowerEndCap";
    }
    /* adding the endcap to its chamber */
    for (let i = 0; i < this.rows[row].chambers.length; i++) {
      const cmb = this.rows[row].chambers[i];
      if (cmb.col === col) {
        cmb[endCapProp] = endCap;
        break;
      }
    }
  }
}

/**
 * ChamberRow Class Model
 */
export class ChamberRow{
  /**
   *
   * @param row {number}
   * @param offSet {number}
   * @param x {number}
   * @param y {number}
   * @param absoluteX {number}
   * @param absoluteY {number}
   * @param pixelOffset {number}
   * @param height {number}
   * @param width {number}
   * @param rotation {number}
   * @param absoluteRotation {number}
   */
  constructor(
    row = 0,
    offSet = 0,
    x = 0,
    y = 0,
    absoluteX = 0,
    absoluteY = 0,
    pixelOffset = 0,
    height = 0,
    width = 0,
    rotation = 0,
    absoluteRotation = 0
  ) {
    this.row = row;
    this.offSet = offSet;
    this.x = x;
    this.y = y;
    this.absltX = absoluteX;
    this.absltY = absoluteY;
    this.pixelOffset = pixelOffset;
    this.height = height;
    this.width = width;
    this.rotation = rotation;
    this.absltRotation = absoluteRotation;
    this.chambers = [];
  }
}

/**
 * Chamber Class Model
 */
export class Chamber{
  /**
   * @param row {number}
   * @param col {number}
   * @param x {number}
   * @param y {number}
   * @param height {number}
   * @param width {number}
   * @param absolutePositionX {number}
   * @param absolutePositionY {number}
   * @param rotation {number}
   * @param absoluteRotation {number}
   */
  constructor(
    row = 0,
    col = 0,
    x = 0,
    y = 0,
    height = 0,
    width = 0,
    absolutePositionX = 0,
    absolutePositionY = 0,
    rotation = 0,
    absoluteRotation = 0
  ) {
    this.row = row;
    this.col = col;
    this.x = x;
    this.y = y;
    this.height = height;
    this.width = width;
    this.absltX = absolutePositionX;
    this.absltY = absolutePositionY;
    this.rotation = rotation;
    this.absltRotation = absoluteRotation;
    this.accessories = [];
    this.upperEndCap = null;
    this.lowerEndCap = null;
    this.isIsolatorArray = [];
    this.isIsolator = false;
  }

  /**
   * Add accessories to chambers
   * @param accessory
   */
  addAccessory( accessory ) {
    const accessoryObj = {
      id: accessory.id,
      name: accessory.name,
      description: accessory.description,
      type: accessory.type,
      color: accessory.color,
      variant: accessory.variant
    };
    if (accessory.size)
      accessoryObj["size"] = accessory.size;
    this.accessories.push(accessoryObj);
  }
}

/**
 * Chamber Class Model
 */
export class EndCap{

  /**
   * @param row {number}
   * @param col {number}
   * @param x {number}
   * @param y {number}
   * @param height {number}
   * @param width {number}
   * @param absolutePositionX {number}
   * @param absolutePositionY {number}
   * @param rotation {number}
   * @param absoluteRotation {number}
   */
  constructor(
    row = 0,
    col = 0,
    x = 0,
    y = 0,
    height = 0,
    width = 0,
    absolutePositionX = 0,
    absolutePositionY = 0,
    rotation = 0,
    absoluteRotation = 0
  ) {
    this.row = row;
    this.col = col;
    this.x = x;
    this.y = y;
    this.height = height;
    this.width = width;
    this.absltX = absolutePositionX;
    this.absltY = absolutePositionY;
    this.rotation = rotation;
    this.absltRotation = absoluteRotation;
    this.accessories = [];
  }

  /**
   * Add accessories to endCap
   * @param accessory
   */
  addAccessory( accessory ) {
    const accessoryObj = {
      id: accessory.id,
      name: accessory.name,
      description: accessory.description,
      type: accessory.type,
      color: accessory.color,
      variant: accessory.variant
    };
    this.accessories.push(accessoryObj);
  }
}

/**
 * Manifold Class Model
 * NOTE: When accessing the model, please request returnPlainObject() to get the Plain Object
 */
export class ManifoldData{
  data = {};

  /**
   * @param manifoldObj {Object} - Manifold JS Plain Structure Object
   */
  constructor(
    manifoldObj
  ) {
    this.data.id = manifoldObj.id;
    this.data.instanceType = manifoldObj.instanceType;
    this.data.style = manifoldObj.style;
    this.data.originNode = manifoldObj.originNode;
    this.data.manifoldSelectionDirect = manifoldObj.manifoldSelectionDirect;
    this.data.totalManifoldFlowRate = manifoldObj.totalManifoldFlowRate;
    this.data.chamberType = manifoldObj.chamberType;
    this.data.rowGap = manifoldObj.rowGap;
    this.data.rowSpacing = manifoldObj.rowSpacing;
    this.data.skipRows = manifoldObj.skipRows;
    this.data.adsUnits = manifoldObj.adsUnits;
    this.data.manifoldType = manifoldObj.manifoldType;
    this.data.legacySizingMethod = manifoldObj.legacySizingMethod;
    this.data.stubCount = manifoldObj.stubCount;

    this.data.nodes = [];                                 // ManifoldNodes Array
  }

  /**
   * Adds a node to the array
   * Note Add only ManifoldNode JS Plain Structure Object using returnPlainObject()
   * @param node {ManifoldNodes}
   */
  addNode( node ) {
    this.data.nodes.push(node);
  }

  /**
   * Returns the Manifold Plain Object
   * @returns {*}
   */
  returnPlainObject() {
    return this.data;
  }
}

/**
 * Manifold Nodes Model
 * NOTE: When accessing the model, please request returnObject() to get the Plain Object
 */
export class ManifoldNodes{
  data = {};
  basinMetadata = {};

  /**
   * Receives Manifold Nodes Model Plain Object
   *
   * @example - Object
   * nodeObj = {
   *   nodeId: "String",
   *   angles: [],
   *   trunkNominalSize: 0,
   *   trunkInvert: 0.0,
   *   trunkFlowRate: 0,
   *   stubNominalSize: 0,
   *   stubInvertPosition: "String",
   *   stubFlowRate: 0.0,
   *   isEccentric: true,
   *   isConcentric: false,
   *   isBasin: True,
   *   trunkMaxCfs: 0.0,
   *   stubMaxCfs: 0.0,
   *   isoDirStubMaxCfs: 0.0
   * };
   * @param nodeObj {Object} - Builds the Node Structure Object
   */
  constructor( nodeObj ) {
    this.data.nodeId = nodeObj.nodeId;                          // "R#C#" (if upper -1 that the C:col value, and lowe +1)
    this.data.angles = nodeObj.angles;                          // Node angles Array
    this.data.trunkNominalSize = nodeObj.trunkNominalSize;      // (Imperial. Manifold Dialog)
    this.data.trunkInvert = nodeObj.trunkInvert;                // (Manifold dataset)
    this.data.trunkFlowRate = nodeObj.trunkFlowRate;            // (Manifold dataset)
    this.data.stubNominalSize = nodeObj.stubNominalSize;        // (Manifold Dialog)
    this.data.stubInvertPosition = nodeObj.stubInvertPosition;  // (Manifold Dialog / Weir)
    this.data.stubInvert = nodeObj.stubInvert;
    this.data.stubFlowRate = nodeObj.stubFlowRate;              // TODO (Check J)
    this.data.isEccentric = nodeObj.isEccentric;                // Calculated in lib/manifolds/utils
    this.data.isConcentric = nodeObj.isConcentric;              // Calculated in utils
    this.data.isBasin = nodeObj.isBasin;                        // Boolean value represents if basin is present
    this.data.trunkMaxCfs = nodeObj.trunkMaxCfs;                // TODO (Check J)
    this.data.isoDirStubMaxCfs = nodeObj.isoDirStubMaxCfs;      // Not using this yet - SKIP for now
    this.data.basinMetadata = null;                             // Attached Basin metadata, null if not attached
  }

  /**
   * Sets Basing Metadata to the node
   *
   * @example
   * basinMetaData = {
   *  basinType: "Round Concrete",
   *  nominalSize: 48,
   *  directStubSize: 24,
   *  bypassMethod: "Eccentric Tee",
   *  eccentricTeeTrunkSize: 0,       // (Will be number / Always 0)
   *  eccentricTeeStubSize: 18,       // (Will be number / Always 0)
   *  outletMethod: "Structure",      // Always "Structure"
   *  underDrainPosition: "Auto",     // Always "Auto"
   *  underDrainSize: true,           // Boolean
   *  minimalDistance: 4,             // Always 4
   * }
   *
   * @param basinMetaData
   * @return {void}
   */
  setBasinMetadata( basinMetaData ) {
    this.data.basinMetadata = {
      basinType: basinMetaData.basinType,                             // (Manifold Dialog)
      nominalSize: basinMetaData.nominalSize,                         // (Structure size - convert to in)
      directStubSize: basinMetaData.directStubSize,                   // (3rd value in manifold size array
      bypassMethod: basinMetaData.bypassMethod,                       // TODO (Check J)
      eccentricTeeTrunkSize: basinMetaData.eccentricTeeTrunkSize,     // (Will be number / Always 0)
      eccentricTeeStubSize: basinMetaData.eccentricTeeStubSize,       // (Will be number / Always 0)
      outletMethod: basinMetaData.outletMethod,                       // Always "Structure"
      // isUnderDrainEnabled: basinMetaData.isUnderDrainEnabled,         // Boolean
      isUnderDrainEnabled: false,         // Boolean
      underDrainPosition: basinMetaData.underDrainPosition,           // Always "Auto"
      underdrainDirMCD: basinMetaData.underdrainDirMCD,               // "UP" || "DOWN"
      underDrainSize: basinMetaData.underDrainSize,                   // Boolean
      minimalDistance: basinMetaData.minimalDistance                 // Always 4
    };
  }

  /**
   * Returns the Node Plain Object
   * @returns {*}
   */
  returnPlainObject() {
    return this.data;
  }

}

/**
 * The main Project Class Model.
 * This will iterate.
 */
export class Project{
  projectType = AUTOMATION_PROJECT_TYPE_CHAMBER;

  constructor() {
    this.region = null;
    this.language = null;
    this.units = null;
    this.email = null;
    this.chamberBed = null;
    this.background = null;
    this.stoneBed = null;
    this.chamberType = null;
    this.chamberEndCapType = null;
    this.metaData = {
      rowSeparation: 0.0,
      scaleValue: 0.0,
      scalePixels: 0,
      requiredVolume : 0.0,
      porousStoneAbove: 0.0,
      porousStoneBelow: 0.0,
      porosityPercent: 0.0,
      totalStorageProvided: 0.0,
      underdrain: false,
      imperviousLiner: false,
      numberOfFeedConnectors: 0,
      linerSqInchOverage: 0,
      chamberBedCenterPoint: {
        x: 0,
        y: 0
      },
      underdrainToggle: false
    };
    this.manifold = [];
  }

  setRegion( value ) {
    this.region = value;
  }

  setLanguage( value ) {
    this.language = value;
  }

  setUnits( value ) {
    this.units = value;
  }

  setEmail( value ) {
    this.email = value;
  }

  setRowSeparation( value ) {
    this.metaData.rowSeparation = value;
  }

  setScaleValue( value ) {
    this.metaData.scaleValue = value;
  }

  setScalePixels( value ) {
    this.metaData.scalePixels = value;
  }

  setStorageVolume( value ) {
    if (value)
      this.metaData.requiredVolume  = value;
    else if (!this.metaData.requiredVolume )
      this.metaData.requiredVolume  = 0;
  }

  setPorousStoneAbove( value ) {
    if (value)
      this.metaData.porousStoneAbove = value;
    else if (!this.metaData.porousStoneAbove)
      this.metaData.porousStoneAbove = 0;
  }

  setPorousStoneBelow( value ) {
    if (value)
      this.metaData.porousStoneBelow = value;
    else if (!this.metaData.porousStoneBelow)
      this.metaData.porousStoneBelow = 0;
  }

  setPorosityPercent( value ) {
    this.metaData.porosityPercent = value;
  }

  setBaseStoneElevation( value ) {
    this.metaData.baseStoneElevation = value;
  }

  setUnderdrain( value ) {
    this.metaData.underdrain = value;
  }

  setUnderdrainToggle( value ) {
    this.metaData.underdrainToggle = value;
  }

  setImperviousLiner( value ) {
    this.metaData.imperviousLiner = value;
  }

  setNumberOfFeedConnectors( value ) {
    this.metaData.numberOfFeedConnectors = value;
  }

  setLinerSqInchOverage( value ) {
    this.metaData.linerSqInchOverage = value;
  }

  setTotalStorageProvided( value ) {
    this.metaData.totalStorageProvided = value;
  }

  /**
   * Adds Manifolds Objects on the project
   * @param manifold {Object}
   * @return {void}
   */
  pushManifolds( manifold ) {
    this.manifold.push(manifold);
  }

  addManifoldsByArray( manifoldArray ) {
    this.manifold = [...this.manifold, ...manifoldArray];
  }

  /**
   * Sets absolute positions for Chamber bed center point as an object
   * @param absolutePositionX {number}
   * @param absolutePositionY {number}
   */
  setChamberBedCenterPoint( absolutePositionX, absolutePositionY ) {
    this.metaData.chamberBedCenterPoint = { absolutePositionX, absolutePositionY };
  }

  /**
   * Sets Chamber Type an object
   * @param chamberType {object}
   */
  setChamberType( chamberType ) {
    this.chamberType = {
      type: chamberType.id,
      length: chamberType.length,
      width: chamberType.width,
      height: chamberType.height,
      storage: chamberType.storage,
      minStorage: chamberType.minStorage,
      weight: chamberType.weight,
      baseStoneHeight: chamberType.baseStoneHeight,
      installedLength: chamberType.installedLength,
      coverStoneHeight: chamberType.coverStoneHeight,
      insTeeMaxDiameter: chamberType.insTeeMaxDiameter,
      insTeeHeightFromtBase: chamberType.insTeeHeightFromtBase
    };
  }

  setChamberEndCapType( chamberEndCapType ) {
    this.chamberEndCapType = {
      length: chamberEndCapType.length,
      width: chamberEndCapType.width,
      height: chamberEndCapType.height,
      storage: chamberEndCapType.storage,
      minStorage: chamberEndCapType.minStorage,
      installedLength: chamberEndCapType.installedLength,
      weight: chamberEndCapType.weight
    };
  }

  addBackground( bg ) {
    this.background = bg;
  }

  addStoneBed( stnBd ) {
    this.stoneBed = stnBd;
  }

  addChamberBed( chmBd ) {
    this.chamberBed = chmBd;
  }

  serialize() {
    /* clean chamber rows */
    this.chamberBed.rows = this.chamberBed.rows.filter(r => r);
    return JSON.stringify(this);
  }
}



// WEBPACK FOOTER //
// ./src/lib/project-build.js